<template lang="pug">
  management-page(:title="$t(`${I18N_PREFIX}.title`)"
                  :creatable="false"
                  :searchable="false"
                  :showHeader="false")
    template(v-slot:body)
      .chart-configuration
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          type="daterange"
                          placeholder="Select date range"
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      aiq-row(:gutter="16")
        aiq-col(:span="12")
          .faq-section
            .faq-section__toolbar
              span.faq-section__title {{ $t(`${I18N_PREFIX}.answered_title`, { count: topAnswered.length }) }}
              .flex-spacer
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
                aiq-button.export(@click="exportTopics('answered')" :disabled="isEmpty(topAnswered)")
                  i.iq-ico-export
            aiq-table.scrollable.clickable(:data="topAnswered"
                                            v-loading="loading"
                                            :empty-text="$t(`${I18N_PREFIX}.empty.answered`)"
                                            :default-sort="DEFAULT_SORT"
                                            :row-class-name="rowClassName('answered')"
                                            @row-click="(row) => selectTopic('answered', row)")
              aiq-table-column(prop="topic_name"
                                :label="$t(`${I18N_PREFIX}.columns.topic`)"
                                min-width="180"
                                sortable
                                label-class-name="is-bordered")
              aiq-table-column(prop="query_count"
                                :label="$t(`${I18N_PREFIX}.columns.count`)"
                                width="110"
                                sortable
                                align="center"
                                label-class-name="is-bordered")
              aiq-table-column(prop="last_seen_at"
                                :label="$t(`${I18N_PREFIX}.columns.last_asked`)"
                                width="170"
                                sortable
                                label-class-name="is-bordered")
                template(v-slot:default="{ row }")
                  span {{ formatDate(row.last_seen_at) }}
        aiq-col(:span="12")
          .faq-section
            .faq-section__toolbar
              span.faq-section__title {{ $t(`${I18N_PREFIX}.unanswered_title`, { count: topUnanswered.length }) }}
              .flex-spacer
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
                aiq-button.export(@click="exportTopics('unanswered')" :disabled="isEmpty(topUnanswered)")
                  i.iq-ico-export
            aiq-table.scrollable.clickable(:data="topUnanswered"
                                            v-loading="loading"
                                            :empty-text="$t(`${I18N_PREFIX}.empty.unanswered`)"
                                            :default-sort="DEFAULT_SORT"
                                            :row-class-name="rowClassName('unanswered')"
                                            @row-click="(row) => selectTopic('unanswered', row)")
              aiq-table-column(prop="topic_name"
                                :label="$t(`${I18N_PREFIX}.columns.topic`)"
                                min-width="180"
                                sortable
                                label-class-name="is-bordered")
              aiq-table-column(prop="query_count"
                                :label="$t(`${I18N_PREFIX}.columns.count`)"
                                width="110"
                                sortable
                                align="center"
                                label-class-name="is-bordered")
              aiq-table-column(prop="last_seen_at"
                                :label="$t(`${I18N_PREFIX}.columns.last_asked`)"
                                width="170"
                                sortable
                                label-class-name="is-bordered")
                template(v-slot:default="{ row }")
                  span {{ formatDate(row.last_seen_at) }}
      .faq-section.topic-detail(v-if="selectedTopic")
        .faq-section__toolbar
          span.faq-section__title {{ $t(`${I18N_PREFIX}.detail_title`, { topic: selectedTopic.topic_name, count: selectedTopic.query_count, interpolation: { escapeValue: false } }) }}
          .flex-spacer
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
            aiq-button.export(@click="exportQueries" :disabled="isEmpty(selectedTopic.queries)")
              i.iq-ico-export
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.close`)" placement="top")
            aiq-button.close-btn(text @click="clearSelection")
              fa-icon(:icon="['fas', 'xmark']" size="lg")
        aiq-table.scrollable(:data="selectedTopic.queries")
          aiq-table-column(prop="query_text"
                            :label="$t(`${I18N_PREFIX}.columns.question`)"
                            min-width="320"
                            label-class-name="is-bordered")
          aiq-table-column(prop="sent_at"
                            :label="$t(`${I18N_PREFIX}.columns.asked_at`)"
                            width="180"
                            sortable
                            label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span {{ formatDate(row.sent_at) }}
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { find, get, isEmpty, snakeCase } from 'lodash';
import {
  downloadAsFile,
  useTimeZone,
  getMetricsTimeZoneAbbr,
  formatDateWithTimezone,
} from '@/libs';
import {
  dstDateRangeUtc,
  datePickerDefaultOptions,
  lastAvailableMonthAndQuarterRange,
  createPickerDateRangeOption,
} from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';

const I18N_PREFIX = 'metrics_tab.smart_assist_tab.questions_tab';
const TOP_N = 10;
const DEFAULT_RANGE_DAYS = 30;
const DEFAULT_SORT = { prop: 'query_count', order: 'descending' };
const DATE_FORMAT = 'MMM DD, YYYY hh:mm A';

const oneMonthDateRange = () => [
  dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
  dayjs().subtract(1, 'day').endOf('day'),
];

export default {
  pageResource: '/metrics',
  components: {
    ManagementPage,
  },
  data() {
    return {
      I18N_PREFIX,
      DEFAULT_SORT,
      dateRange: oneMonthDateRange(),
      answered: [],
      unanswered: [],
      loading: false,
      selectedSide: null,
      selectedTopicId: null,
      timezone: '',
      tzAbbr: '',
    };
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.tzAbbr = getMetricsTimeZoneAbbr(this.timezone);
    this.fetchFaq();
  },
  computed: {
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    topAnswered() {
      return this.answered.slice(0, TOP_N);
    },
    topUnanswered() {
      return this.unanswered.slice(0, TOP_N);
    },
    selectedTopic() {
      if (!this.selectedSide || this.selectedTopicId === null) return null;
      const list = this.selectedSide === 'answered' ? this.topAnswered : this.topUnanswered;
      return find(list, { topic_id: this.selectedTopicId }) || null;
    },
  },
  watch: {
    dateRangeUtc() {
      this.fetchFaq();
    },
  },
  methods: {
    isEmpty,
    formatDate(d) {
      return `${formatDateWithTimezone(d, this.timezone, DATE_FORMAT)} ${this.tzAbbr}`;
    },
    selectTopic(side, row) {
      if (this.selectedSide === side && this.selectedTopicId === row.topic_id) {
        this.clearSelection();
        return;
      }
      this.selectedSide = side;
      this.selectedTopicId = row.topic_id;
    },
    clearSelection() {
      this.selectedSide = null;
      this.selectedTopicId = null;
    },
    rowClassName(side) {
      return ({ row }) => (
        this.selectedSide === side && this.selectedTopicId === row.topic_id ? 'is-selected' : ''
      );
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}.csv`;
    },
    exportTopics(side) {
      const list = side === 'answered' ? this.topAnswered : this.topUnanswered;
      const rows = list.map(t => ({
        Topic: t.topic_name,
        Count: t.query_count,
        'Last asked': this.formatDate(t.last_seen_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(`smart_assist_${side}_topics`),
        data: rows,
        dataType: 'table',
      });
    },
    exportQueries() {
      const topic = this.selectedTopic;
      if (!topic) return;
      const rows = get(topic, 'queries', []).map(q => ({
        Question: q.query_text,
        'Asked at': this.formatDate(q.sent_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(`smart_assist_questions_${snakeCase(topic.topic_name)}`),
        data: rows,
        dataType: 'table',
      });
    },
    async fetchFaq() {
      const [period_start, period_end] = this.dateRangeUtc;
      if (!(period_start && period_end)) {
        return;
      }
      this.loading = true;
      this.clearSelection();
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          'smart_assist_faq',
          { period_start, period_end, limit: TOP_N },
        ]);
        const dataset = get(data, 'dataset', []);
        this.answered = get(find(dataset, { metric: 'smart_assist_faq_answered' }), 'data_points', []);
        this.unanswered = get(find(dataset, { metric: 'smart_assist_faq_unanswered' }), 'data_points', []);
      } catch (err) {
        this.$aiq.notify.error(this.$t(`${I18N_PREFIX}.errors.load_failed`));
        this.answered = [];
        this.unanswered = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.chart-configuration {
  display: flex;
  margin-bottom: 24px;
}

.time-range-filter {
  position: relative;
  margin-left: auto;
}

.faq-section {
  margin-bottom: 16px;
}

.faq-section__toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.faq-section__title {
  font-size: 14px;
  font-weight: 600;
  color: $aiq-text-active;
}

.flex-spacer {
  flex: 1;
}

:deep(.el-button.export) {
  height: 30px;
  width: 40px;
  padding: 0;
  margin-left: 8px;
}

:deep(.el-button.close-btn) {
  height: 30px;
  width: 30px;
  padding: 0;
  margin-left: 4px;
}

.topic-detail {
  margin-top: 16px;
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-mixins.scss";
@import "../../../../styles/aiq-variables.scss";

.faq-section {
  .scrollable .el-table__body-wrapper {
    @include scrollable(calc(100vh - 280px));
  }

  &.topic-detail .scrollable .el-table__body-wrapper {
    @include scrollable(420px);
  }
}

.clickable {
  .el-table__row {
    cursor: pointer;
  }
  .el-table__row.is-selected td {
    background-color: $aiq-bgc-sidebar-item-hover !important;
  }
}
</style>
